import { SuspenseSelect } from '@/components/Suspense';
import {
  BaseSelectProps,
  useSetCacheQueryData,
} from '@/context/SelectComponent/Base';
import { useModelsToOptionsWithCacheItems } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { getPageData } from '@/utils/getPageData';
import {
  FormSelect,
  FormSelectProps,
  SelectProps,
} from '@moneyforward/ap-frontend-components';
import { Dept as DeptModel, useGetDeptsSuspenseInfinite } from 'ap-openapi';
import { memo, startTransition, useCallback, useMemo, useState } from 'react';
import { FieldValues } from 'react-hook-form';

export type Props<TFieldValues extends FieldValues> = {
  selectProps?: Omit<
    FormSelectProps<TFieldValues>['selectProps'],
    'options' | 'comboBox'
  >;
  query?: string;
  per?: number;
  validateMaxCount?: number;
  comboBox?: boolean;
  mode?: 'single' | 'multiple';
  setLabelField?: (label: string) => void;
  displayInactiveOptions?: boolean;
} & Omit<FormSelectProps<TFieldValues>, 'selectProps' | 'label'> &
  BaseSelectProps;

const _DeptSelect = <TFieldValues extends FieldValues>({
  selectProps: _selectProps,
  defaultOptions: _defaultOptions,
  onChangeAfter,
  cacheQueryKey,
  per = 25,
  comboBox = true,
  mode = 'single',
  setLabelField,
  displayInactiveOptions,
  ...rest
}: Props<TFieldValues>) => {
  const defaultOptions = useMemo(
    () => _defaultOptions ?? [],
    [_defaultOptions]
  );
  const { t } = useTranslation();
  const [query, setQuery] = useState<string | undefined>(undefined);
  const {
    data: apiData,
    error,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetDeptsSuspenseInfinite(
    {
      query,
      per: per,
      display_inactive_options: displayInactiveOptions,
    },
    {
      query: {
        getNextPageParam: (page) => {
          const url = new URL(page.data.next ?? '/', location.href);
          if (!url.searchParams.has('page')) return undefined;
          return Number(url.searchParams.get('page'));
        },
      },
      request: {
        headers: {
          agentApplicantResource: 'dept',
        },
      },
    }
  );
  if (error) throw error;
  const data = useMemo(() => getPageData(apiData, 'depts'), [apiData]);
  const options = useModelsToOptionsWithCacheItems<DeptModel>(
    data,
    cacheQueryKey,
    query,
    defaultOptions
  );
  const wrapOnChangeAfter = useCallback(
    (value: string | number | (string | number)[]) => {
      const selectedOption = options.find(
        (option) => option.value === String(value)
      );
      onChangeAfter?.(value);
      setLabelField?.(selectedOption?.label ?? '');
    },
    [setLabelField, options, onChangeAfter]
  );
  const fetchMore = useCallback(
    async (callback: () => void) => {
      if (hasNextPage) {
        fetchNextPage();
      }
      callback();
    },
    [fetchNextPage, hasNextPage]
  );
  const onSearch = useCallback((value: string) => {
    startTransition(() => {
      setQuery(value);
    });
  }, []);
  const selectProps: SelectProps = useMemo(
    () => ({
      onSearchDebounceWait: 600,
      comboBox: comboBox,
      placeholder: t('dept_placeholder'),
      inputPlaceholder: t('dept_input_placeholder'),
      ..._selectProps,
      options: options,
      onSearch: onSearch,
      loading: isLoading,
      mode: mode,
      fetchMore: fetchMore,
      selectAll: undefined,
      selectAllLabel: undefined,
    }),
    [_selectProps, comboBox, fetchMore, isLoading, mode, onSearch, options, t]
  );
  useSetCacheQueryData(cacheQueryKey, data);

  return (
    <FormSelect<TFieldValues>
      selectProps={selectProps}
      {...rest}
      onChangeAfter={wrapOnChangeAfter}
    />
  );
};
_DeptSelect.displayName = '_DeptSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const DeptSelect = memo((props) => (
  <SuspenseSelect>
    <_DeptSelect {...props} />
  </SuspenseSelect>
)) as typeof _DeptSelect;
DeptSelect.displayName = 'DeptSelect';
